import React, {useState} from 'react';
import {TextField, UrlField} from "../Form";
import {classNames, toTitleCase} from "../../../services/string";
import {Scheme} from "../Form/Control";
import MultiSelector from "../MultiSelector";
import {LinkOptions} from 'types/Link';
import {Item} from "../MultiSelector/MultiSelector";
import IconName from "../../../types/IconName";
import {useField} from "formik";

// Helpers ----

function getOptionItems(options: LinkOptions[], scheme: Scheme): Array<Item> {
  return options
    .filter(option => {
      // If the option is AUTO_LOGIN, it should only be enabled for HTTPS schemes
      if (option === LinkOptions.AUTO_LOGIN) return scheme === 'https';

      // Otherwise, all options are valid
      return true;
    })
    .map(option => ({
      id: option,
      name: toTitleCase(option),
      icon: IconName.COG
    }));
}

// Types ----

type Props = {
  name?: string
  textName?: string
  placeholderText?: string
  urlName?: string
  placeholderUrl?: string
  optionsName?: string
  options?: LinkOptions[]
  dark?: boolean
  schemes?: Scheme[]
  disabled?: boolean
}

// Component ----

export default function LinkEditor({name, textName, placeholderText, urlName, placeholderUrl, optionsName, options = [], dark, schemes, disabled}: Props) {
  const [scheme, setScheme] = useState<Scheme>('https');

  const textFieldName = [name, textName ?? 'text'].filter(Boolean).join('.');
  const urlFieldName = [name, urlName ?? 'url'].filter(Boolean).join('.');
  const optionsFieldName = [name, optionsName ?? 'options'].filter(Boolean).join('.');

  const [optionsField,,optionsFieldHelpers] = useField(optionsFieldName);
  const items = getOptionItems(options, scheme);

  return (
    <fieldset className={classNames(
      "rounded-md pb-4 px-4 pt-2 grid grid-cols-1 md:grid-cols-8 gap-4",
      dark? "bg-gray-100" : "bg-gray-50"
    )}>
      <div className="md:col-span-3">
        <TextField
          name={textFieldName}
          label="Label *"
          hint='100 chars max'
          placeholder={placeholderText ?? "Find out more"}
          disabled={disabled}
          maxLength={100}
        />
      </div>

      <div className="md:col-span-5">
        <UrlField
          name={urlFieldName}
          label="URL *"
          placeholder={placeholderUrl ?? "www.n-gage.io"}
          schemes={schemes}
          disabled={disabled}
          onChangeScheme={scheme => setScheme(scheme)}
        />
      </div>

      {!!items?.length &&
        <div className="md:col-span-8">
          <MultiSelector
            label='Options'
            selectedIds={optionsField.value}
            items={items}
            onChange={selectedItems => optionsFieldHelpers.setValue(selectedItems?.map(value => value.id as LinkOptions))}
          />
        </div>
      }
    </fieldset>
  )
}